import {
  TITLES,
  DESCRIPTIONS,
  LONG_HEADLINES,
  BUSINESS_NAME_PLACEHOLDER,
  DESTINATION_URL_PLACEHOLDER,
  DESTINATION_TYPE_PLACEHOLDER,
  LOGO_URLS_PLACEHOLDER,
  VIDEOS_OF_CAMPAIGN_PLACEHOLDER,
  SEARCH_THEMES_PLACEHOLDER,
  CALL_TO_ACTION_PLACEHOLDER,
  MEDIA_URLS_PLACEHOLDER,
  HEADLINES,
  NETWORK_DISPLAY_SETTING_PLACEHOLDER,
  GOOGLE_AI_MAX_ENABLE_QUESTION,
  SITE_LINKS_PLACEHOLDER,
  SITE_LINK_TEXT_PLACEHOLDER,
  SITE_LINK_FINAL_URL_PLACEHOLDER,
  CALLOUTS_PLACEHOLDER,
  CONVERSION_ACTIONS_PLACEHOLDER,
  APP_STORE_PLACEHOLDER,
  APP_PLACEHOLDER,
  TRACKING_OFFLINE_EVENTS_PLACEHOLDER,
  MERCHANT_CENTER_ID_PLACEHOLDER,
  BIDDING_STRATEGY_TYPE_PLACEHOLDER,
  TYPE_BUDGET_PLACEHOLDER,
  USE_CAMPAIGN_BUDGET_PLACEHOLDER,
  KEYWORDS_PLACEHOLDER,
  KEYWORDS_NEGATIVE_PLACEHOLDER,
  LOCATIONS_PLACEHOLDER,
  EXCLUDED_LOCATIONS_PLACEHOLDER,
  LANGUAGE_PLACEHOLDER,
  OBJECTIVE_PLACEHOLDER,
  BUDGET_PLACEHOLDER,
  START_DATE_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  TITLE_PLACEHOLDER,
  DESCRIPTION_PLACEHOLDER,
  LONG_HEADLINE_PLACEHOLDER,
} from "../../../i18n/keysTranslations";
import { MAP_TRADUCCIONES } from "../../../utils/constants";

/**
 * Extracts the field name and index from an error path
 * Examples:
 * - "assetsGroups.[0].titles" -> { fieldName: "titles", index: null }
 * - "assetsGroups.[0].titles.[2].value" -> { fieldName: "titles", index: 2 }
 * - "adsGroups.[0].ads.[1].destinationUrls" -> { fieldName: "destinationUrls", index: null }
 * - "platformsData.google.objective" -> { fieldName: "objective", index: null }
 *
 * @param {string} path - The error path
 * @returns {{fieldName: string|null, index: number|null}} Object with field name and index
 */
export const extractFieldNameFromPath = (path) => {
  if (!path) return { fieldName: null, index: null };

  const parts = path.split(".");
  let fieldName = null;
  let index = null;

  // Find the last non-index part (not matching [number]) that is not a nested property
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];

    // Skip array indices and nested properties
    if (
      part.match(/^\[\d+\]$/) ||
      part === "value" ||
      (part === "type" && i > 0 && parts[i - 1] === "biddingStrategy") ||
      part === "id" ||
      part === "ref"
    ) {
      continue;
    }

    // Found the field name
    fieldName = part;

    // Check if there's an index right after this field name (next part in the path)
    if (i < parts.length - 1) {
      const nextPart = parts[i + 1];
      const nextIndexMatch = nextPart?.match(/^\[(\d+)\]$/);
      if (nextIndexMatch) {
        index = parseInt(nextIndexMatch[1], 10);
      }
    }

    break;
  }

  return { fieldName, index };
};

/**
 * Gets the translated label for a field name
 *
 * @param {string} fieldName - The field name (e.g., "titles", "descriptions")
 * @param {Function} t - Translation function
 * @param {number|null} index - Optional index for array fields (0-based)
 * @returns {string} The translated label or the field name if no translation found
 */
export const getFieldLabel = (fieldName, t, index = null) => {
  if (!fieldName || !t) return fieldName;

  const capitalizeFirstLetter = (text) => {
    if (!text || typeof text !== "string") return text;
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  };

  // Map plural field names to their singular form for indexed items
  const singularFieldLabels = {
    titles: t(TITLE_PLACEHOLDER),
    descriptions: t(DESCRIPTION_PLACEHOLDER),
    longHeadlines: t(LONG_HEADLINE_PLACEHOLDER),
    headlines: t(HEADLINES), // Assuming HEADLINES is already singular or we use it as is
  };

  const fieldLabels = {
    // Campaign/Platform fields
    budget: t(BUDGET_PLACEHOLDER),
    typeBudget: t(TYPE_BUDGET_PLACEHOLDER),
    useCampaignBudget: t(USE_CAMPAIGN_BUDGET_PLACEHOLDER),
    locations: t(LOCATIONS_PLACEHOLDER),
    excludedLocations: t(EXCLUDED_LOCATIONS_PLACEHOLDER),
    language: t(LANGUAGE_PLACEHOLDER),
    objective: t(OBJECTIVE_PLACEHOLDER),
    startDate: t(START_DATE_PLACEHOLDER),
    endDate: t(END_DATE_PLACEHOLDER),
    businessName: t(BUSINESS_NAME_PLACEHOLDER),
    networkSettings: t(NETWORK_DISPLAY_SETTING_PLACEHOLDER),
    enableAIMax: t(GOOGLE_AI_MAX_ENABLE_QUESTION),
    biddingStrategy: t(BIDDING_STRATEGY_TYPE_PLACEHOLDER),
    siteLinks: t(SITE_LINKS_PLACEHOLDER),
    linkText: t(SITE_LINK_TEXT_PLACEHOLDER),
    finalUrl: t(SITE_LINK_FINAL_URL_PLACEHOLDER),
    callOuts: t(CALLOUTS_PLACEHOLDER),
    conversionActions: t(CONVERSION_ACTIONS_PLACEHOLDER),
    merchantCenterID: t(MERCHANT_CENTER_ID_PLACEHOLDER),

    // Ad Group fields
    keywords: t(KEYWORDS_PLACEHOLDER),
    keywordsNegative: t(KEYWORDS_NEGATIVE_PLACEHOLDER),
    destinationType: t(DESTINATION_TYPE_PLACEHOLDER),
    trackingOfflineID: t(TRACKING_OFFLINE_EVENTS_PLACEHOLDER),
    appStore: t(APP_STORE_PLACEHOLDER),
    appID: t(APP_PLACEHOLDER),

    // Ad fields
    callToAction: t(CALL_TO_ACTION_PLACEHOLDER),
    destinationUrls: t(DESTINATION_URL_PLACEHOLDER),
    titles: t(TITLES),
    descriptions: t(DESCRIPTIONS),
    headlines: t(HEADLINES),
    mediaUrls: t(MEDIA_URLS_PLACEHOLDER),

    // Assets Group fields
    destinationUrl: t(DESTINATION_URL_PLACEHOLDER),
    logoUrls: t(LOGO_URLS_PLACEHOLDER),
    videoUrls: t(VIDEOS_OF_CAMPAIGN_PLACEHOLDER),
    longHeadlines: t(LONG_HEADLINES),
    searchThemes: t(SEARCH_THEMES_PLACEHOLDER),
  };

  // If there's an index and we have a singular form, use it with the index number
  if (index !== null && index !== undefined && singularFieldLabels[fieldName]) {
    return `${singularFieldLabels[fieldName]} ${index + 1}`;
  }

  // First try explicit placeholder-based mappings
  if (fieldLabels[fieldName]) {
    return fieldLabels[fieldName];
  }

  // Then try direct translation if the fieldName is itself a translation key (e.g. "context")
  const directTranslation = t(fieldName);
  if (directTranslation && directTranslation !== fieldName) {
    return directTranslation;
  }

  // Then fallback to MAP_TRADUCCIONES (used across the app for field labels)
  const mapTranslationKey = MAP_TRADUCCIONES?.[fieldName];
  if (mapTranslationKey) {
    const translated = t(mapTranslationKey);
    if (translated && translated !== mapTranslationKey) {
      return capitalizeFirstLetter(translated);
    }
  }

  // Final fallback: humanize the fieldName (camelCase / snake_case)
  const humanized = String(fieldName)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  return capitalizeFirstLetter(humanized);
};
